/**
 * provenanceChip.js — Item 1.6a utility for the AI chat provenance chip path.
 *
 * Pre-D.7 the chip filter at Home.js:408 was hardcoded to
 * `source_type === 'chat_capture'`. Item 1.6a widens that to an explicit
 * allowlist so XMEK admins see chips for their interview / supplier-research
 * / news / cross-synthesis sourced assertions on every AI answer that draws
 * on them. The dominant `upload` source (~36k rows at XMEK) is intentionally
 * deferred — its deep-link target needs its own design conversation. The
 * `system` source is permanently excluded (platform-internal, not user-facing).
 *
 * Generic-then-refine deep-link strategy (§7.19): v1 routes every chip to the
 * entity profile derived from `entity_type` + `entity_id` (or `entity_name`
 * for supplier routes which key on name). Per-source-specific routing
 * (interview detail view, news entry, etc.) is a follow-on enhancement.
 *
 * §7.16 defensive NULL handling throughout — entity_id can be NULL on
 * intentional market/industry-scope assertions (447 XMEK rows per Pass 4).
 * Chips for those source_types still render but stay unclickable (null href).
 *
 * §7.20 (Pass 3 follow-up): the original 1.6a filter widen at Home.js:408 was
 * audited; the downstream label path at Home.js:452 was not. That call routed
 * through `formatAssertionChipLabel` → `predicateFallback` → a `PREDICATE_LABEL`
 * dict containing only D.7's 8 chat_capture predicates, so every news_ingest /
 * news_digest / supplier_research / interview / cross_synthesis chip fell
 * through to the universal "Captured intelligence" fallback. `getProvenanceLabel`
 * (below) closes that gap with per-source delegation: chat_capture keeps the
 * existing D.7 rendering; everything else gets an entity+predicate string.
 */

import { formatAssertionChipLabel } from './captureLabel';


// Explicit allowlist per Pass 1 §1.8. Adding a new source_type means
// thinking about its label, rationale, and deep-link target deliberately —
// the blocklist alternative would have silently rendered upload's 36k rows
// as chips, which is bad UX and bad deep-link story.
export const PROVENANCE_SOURCE_TYPES = Object.freeze([
  'chat_capture',
  'interview',
  'supplier_research',
  'news_ingest',
  'news_digest',
  'cross_synthesis',
]);


// Per-source rationale strings shown in the CaptureChip tooltip. Each one
// matches the chip's source_type so the user can tell where the cited fact
// came from at a glance. Confidence is appended at call time.
const RATIONALE_PREFIX = {
  chat_capture: 'From your captured intelligence',
  interview: 'From your XMEK interview',
  supplier_research: 'From supplier research',
  news_ingest: 'From recent news',
  news_digest: 'From the daily news digest',
  cross_synthesis: 'From cross-interview synthesis',
};


/**
 * Filter function for the assertions_cited[] array in Home.js. Returns true
 * for entries that should render as chips, false to drop them.
 *
 * chat_capture preserves the D.7 candidate_id != null guard — its chip's
 * sole purpose is to deep-link to /captures, so a missing candidate_id makes
 * the chip useless. All other allowed source_types render regardless of
 * candidate_id (which is always null for non-chat_capture).
 */
export function shouldRenderChip(a) {
  if (!a || typeof a !== 'object') return false;
  if (!PROVENANCE_SOURCE_TYPES.includes(a.source_type)) return false;
  if (a.source_type === 'chat_capture' && a.candidate_id == null) return false;
  return true;
}


/**
 * Per-source-type rationale text for the CaptureChip tooltip. Mirrors the
 * D.7 phrasing pattern: "{source phrase} · confidence {N.NN}".
 *
 * Unknown source_types (anything not in the allowlist) won't reach this
 * function in normal flow because shouldRenderChip filters them out first,
 * but the defensive fallback handles the case anyway.
 */
export function getProvenanceRationale(a) {
  const prefix = (a && RATIONALE_PREFIX[a.source_type]) || 'Cited source';
  const confRaw = a && typeof a.confidence === 'number' ? a.confidence : 0;
  return `${prefix} · confidence ${confRaw.toFixed(2)}`;
}


/**
 * Returns the visible chip face label (NOT the tooltip — that's
 * getProvenanceRationale). Per-source delegation:
 *
 *   chat_capture  → existing `formatAssertionChipLabel` (preserves D.7's
 *                   8-predicate templated rendering exactly; no regression)
 *   everything else → "{entity_name} · {predicate prettified}"
 *                     with snake_case → space-separated lowercase phrasing
 *                     (e.g. 'market_price_movement' → 'market price movement')
 *
 * Defensive cascade for the non-chat_capture branch:
 *   - entity_name AND predicate present → "{entity_name} · {predicate phrase}"
 *   - predicate only                    → "{predicate phrase}"
 *   - entity_name only                  → "{entity_name}"
 *   - both missing                      → "Source citation"
 *
 * 'market' as an entity_name is treated as present (it's a meaningful scope,
 * not garbage) — news_ingest chips will read "market · market price movement"
 * which is honest if a bit redundant. v2 polish can dedupe.
 */
function prettifyPredicate(predicate) {
  if (typeof predicate !== 'string' || predicate.length === 0) return null;
  return predicate.replace(/_/g, ' ').trim().toLowerCase() || null;
}

export function getProvenanceLabel(a) {
  if (!a || typeof a !== 'object') return 'Source citation';

  if (a.source_type === 'chat_capture') {
    return formatAssertionChipLabel({
      entity_name: a.entity_name,
      predicate: a.predicate,
    });
  }

  const name = typeof a.entity_name === 'string' ? a.entity_name.trim() : '';
  const phrase = prettifyPredicate(a.predicate);

  if (name && phrase) return `${name} · ${phrase}`;
  if (phrase) return phrase;
  if (name) return name;
  return 'Source citation';
}


/**
 * Returns the deep-link URL for a chip click, or null if the assertion has
 * no resolvable target (NULL entity_id on a market-scope row, unknown
 * entity_type, missing candidate_id on chat_capture, etc.). Callers in
 * Home.js should pass the result to navigate() only when non-null and
 * suppress the click handler otherwise.
 *
 * Route map sourced from frontend/src/App.js:73–113:
 *   chat_capture           → /captures?candidate_id=X    (preserves D.7)
 *   entity_type=supplier   → /suppliers/<entity_name>    (route keys on name)
 *   entity_type=account    → /customers?account=<entity_id>#notes
 *   entity_type=material   → /materials/<entity_id>
 *   entity_type=company    → /companies/<entity_id>
 *   anything else / NULL   → null  (chip renders, click is suppressed)
 */
export function getProvenanceDeepLink(a) {
  if (!a || typeof a !== 'object') return null;

  if (a.source_type === 'chat_capture') {
    if (a.candidate_id == null) return null;
    return `/captures?candidate_id=${encodeURIComponent(a.candidate_id)}`;
  }

  const et = a.entity_type;
  // Supplier routes key on name, not id — see App.js:111.
  if (et === 'supplier') {
    if (!a.entity_name) return null;
    return `/suppliers/${encodeURIComponent(a.entity_name)}`;
  }
  // All other entity_types route on entity_id. NULL guard per §7.16.
  if (a.entity_id == null) return null;
  if (et === 'account') return `/customers?account=${encodeURIComponent(a.entity_id)}#notes`;
  if (et === 'material') return `/materials/${encodeURIComponent(a.entity_id)}`;
  if (et === 'company') return `/companies/${encodeURIComponent(a.entity_id)}`;
  return null;
}
